import type {
  PlaybackBucket,
  PlaybackChunkV1,
  PlaybackTransport,
  PlaybackVideoRendition,
  PlaybackVideoRenditionSeconds
} from "@recital-master/shared";
import { PLAYBACK_BUCKET_SECONDS } from "@recital-master/shared";

import { isPlaybackTimeDiscontinuity } from "./playbackProgress";

export const PLAYBACK_TELEMETRY_FLUSH_SECONDS = 60;

export type ReportedVideoRendition = {
  danceId: string | null;
  rendition: PlaybackVideoRendition | null;
} | null;

/**
 * Attribute a player rendition only when both sides identify the same dance.
 * During initial catalog loading both IDs are absent; that is not a match.
 */
export function videoRenditionForDance(
  reported: ReportedVideoRendition,
  danceId: string | null
): PlaybackVideoRendition | null {
  if (!reported || !danceId || reported.danceId !== danceId) return null;
  return reported.rendition;
}

const UNKNOWN_VIDEO_RENDITION: PlaybackVideoRendition = {
  width: null,
  height: null,
  codec: "unknown"
};

function normalizeVideoRendition(
  rendition: PlaybackVideoRendition | null
): PlaybackVideoRendition | null {
  if (!rendition) return null;
  const width = rendition.width != null && Number.isFinite(rendition.width) && rendition.width > 0
    ? Math.round(rendition.width)
    : null;
  const height = rendition.height != null && Number.isFinite(rendition.height) && rendition.height > 0
    ? Math.round(rendition.height)
    : null;
  return { width, height, codec: rendition.codec };
}

function videoRenditionKey(rendition: PlaybackVideoRendition | null) {
  const value = rendition ?? UNKNOWN_VIDEO_RENDITION;
  return `${value.width ?? "unknown"}x${value.height ?? "unknown"}:${value.codec}`;
}

export type PlaybackAccumulatorIdentity = {
  sessionId: string;
  playId: string;
  danceId: string;
  performanceId: string;
  recitalId: string;
  transport: PlaybackTransport;
  entryPositionSeconds: number;
};

/** Pure accumulator; networking and lifecycle delivery live in its hook. */
export class PlaybackChunkAccumulator {
  private readonly identity: Omit<PlaybackAccumulatorIdentity, "transport">;
  private transport: PlaybackTransport;
  private sequence = 0;
  private previousPosition: number | null = null;
  private previousObservedAtMs: number | null = null;
  private wasPlaying = false;
  private playbackRate = 1;
  private chunkEntryPosition: number | null = null;
  private buckets = new Map<number, number>();
  private watchedSeconds = 0;
  private videoRendition: PlaybackVideoRendition | null = null;
  private videoRenditionSeconds = new Map<string, PlaybackVideoRenditionSeconds>();

  constructor(identity: PlaybackAccumulatorIdentity) {
    this.identity = identity;
    this.transport = identity.transport;
    this.previousPosition = Math.max(0, identity.entryPositionSeconds);
  }

  observe(positionSeconds: number, observedAtMs: number): PlaybackChunkV1[] {
    if (!Number.isFinite(positionSeconds) || !Number.isFinite(observedAtMs)) return [];
    const position = Math.max(0, positionSeconds);
    if (this.previousPosition == null || this.previousObservedAtMs == null) {
      this.anchor(position, observedAtMs);
      return [];
    }

    if (!this.wasPlaying) {
      this.anchor(position, observedAtMs);
      return [];
    }

    const intervalMs = Math.max(0, observedAtMs - this.previousObservedAtMs);
    if (
      isPlaybackTimeDiscontinuity(
        this.previousPosition,
        position,
        intervalMs,
        this.playbackRate
      )
    ) {
      const chunk = this.flush();
      this.anchor(position, observedAtMs);
      return chunk ? [chunk] : [];
    }

    if (position > this.previousPosition) {
      this.addInterval(this.previousPosition, position);
    }
    this.anchor(position, observedAtMs);
    const chunk = this.watchedSeconds >= PLAYBACK_TELEMETRY_FLUSH_SECONDS
      ? this.flush()
      : null;
    return chunk ? [chunk] : [];
  }

  setPlaying(
    playing: boolean,
    positionSeconds: number,
    observedAtMs: number
  ): PlaybackChunkV1[] {
    if (!Number.isFinite(positionSeconds) || !Number.isFinite(observedAtMs)) return [];
    const position = Math.max(0, positionSeconds);
    if (playing === this.wasPlaying) {
      if (this.previousObservedAtMs == null) this.anchor(position, observedAtMs);
      return [];
    }

    const chunks = this.wasPlaying
      ? this.observe(position, observedAtMs)
      : [];
    this.wasPlaying = playing;
    this.anchor(position, observedAtMs);
    if (!playing) {
      const chunk = this.flush();
      if (chunk) chunks.push(chunk);
    }
    return chunks;
  }

  setTransport(
    transport: PlaybackTransport,
    positionSeconds: number,
    observedAtMs: number
  ): PlaybackChunkV1[] {
    if (transport === this.transport) return [];
    const chunks = this.wasPlaying
      ? this.observe(positionSeconds, observedAtMs)
      : [];
    const chunk = this.flush();
    if (chunk) chunks.push(chunk);
    this.transport = transport;
    this.videoRendition = null;
    this.anchor(Math.max(0, positionSeconds), observedAtMs);
    return chunks;
  }

  setPlaybackRate(playbackRate: number) {
    this.playbackRate = Number.isFinite(playbackRate) && playbackRate > 0
      ? playbackRate
      : 1;
  }

  setVideoRendition(
    rendition: PlaybackVideoRendition | null,
    positionSeconds: number,
    observedAtMs: number
  ): PlaybackChunkV1[] {
    if (!Number.isFinite(positionSeconds) || !Number.isFinite(observedAtMs)) return [];
    const normalized = normalizeVideoRendition(rendition);
    if (videoRenditionKey(normalized) === videoRenditionKey(this.videoRendition)) return [];
    const chunks = this.wasPlaying
      ? this.observe(positionSeconds, observedAtMs)
      : [];
    this.videoRendition = normalized;
    this.anchor(Math.max(0, positionSeconds), observedAtMs);
    return chunks;
  }

  flushAt(positionSeconds: number, observedAtMs: number): PlaybackChunkV1[] {
    if (!Number.isFinite(positionSeconds) || !Number.isFinite(observedAtMs)) return [];
    const chunks = this.wasPlaying
      ? this.observe(positionSeconds, observedAtMs)
      : [];
    const chunk = this.flush();
    if (chunk) chunks.push(chunk);
    this.anchor(Math.max(0, positionSeconds), observedAtMs);
    return chunks;
  }

  flush(): PlaybackChunkV1 | null {
    if (this.watchedSeconds <= 0 || this.buckets.size === 0) return null;
    const buckets: PlaybackBucket[] = [...this.buckets]
      .sort(([left], [right]) => left - right)
      .map(([startSecond, watchedSeconds]) => ({ startSecond, watchedSeconds }));
    const chunk: PlaybackChunkV1 = {
      version: 1,
      bucketSeconds: PLAYBACK_BUCKET_SECONDS,
      sessionId: this.identity.sessionId,
      playId: this.identity.playId,
      sequence: this.sequence,
      danceId: this.identity.danceId,
      performanceId: this.identity.performanceId,
      recitalId: this.identity.recitalId,
      transport: this.transport,
      entryPositionSeconds: this.chunkEntryPosition ?? this.previousPosition ?? this.identity.entryPositionSeconds,
      watchedSeconds: this.watchedSeconds,
      buckets,
      videoRenditionSeconds: [...this.videoRenditionSeconds.values()]
    };
    this.sequence += 1;
    this.buckets.clear();
    this.watchedSeconds = 0;
    this.videoRenditionSeconds.clear();
    this.chunkEntryPosition = null;
    return chunk;
  }

  private addInterval(start: number, end: number) {
    if (this.chunkEntryPosition == null) this.chunkEntryPosition = start;
    let cursor = start;
    while (cursor < end) {
      const bucketStart = Math.floor(cursor / PLAYBACK_BUCKET_SECONDS) * PLAYBACK_BUCKET_SECONDS;
      const bucketEnd = bucketStart + PLAYBACK_BUCKET_SECONDS;
      const watched = Math.min(end, bucketEnd) - cursor;
      this.buckets.set(bucketStart, (this.buckets.get(bucketStart) ?? 0) + watched);
      this.watchedSeconds += watched;
      const rendition = this.videoRendition ?? UNKNOWN_VIDEO_RENDITION;
      const renditionKey = videoRenditionKey(rendition);
      const existing = this.videoRenditionSeconds.get(renditionKey);
      this.videoRenditionSeconds.set(renditionKey, {
        ...rendition,
        watchedSeconds: (existing?.watchedSeconds ?? 0) + watched
      });
      cursor += watched;
    }
  }

  private anchor(positionSeconds: number, observedAtMs: number) {
    this.previousPosition = positionSeconds;
    this.previousObservedAtMs = observedAtMs;
  }
}
